import type { IProjectFloor } from '@apis/contractorAnalysis';

/**
 * Ascending sort over a project floor catalog. Mirrors trusstor's
 * `shared/utils/sort.utils.ts:sortFloors`: bucket floors by id shape,
 * sort each bucket on its natural ordering, then concatenate in a fixed
 * order. The result is what trusstor calls "by location" — basements
 * (negative numbers) → digit floors ascending → mixed digit+letter
 * floors → preparation area → everything else.
 *
 *   1. digits-only (`^-?\d+$`)   — sorted numerically (so `-5 -4 -3 -2 -1 1 2 ... 20`,
 *                                  not lexicographically `1 10 11 ... 2 20`).
 *   2. digits+letters             — sort by trailing letter, then numeric prefix
 *                                   (`1A 2A 1B 2B`).
 *   3. preparation area (`pa`)    — single row.
 *   4. everything else            — alphabetical by floorId as a stable tail
 *                                   so non-catalog ids (e.g. floors that
 *                                   appear on productivity records but
 *                                   aren't in the catalog) still land in
 *                                   a deterministic spot.
 *
 * The original trusstor algorithm filters out `storage` floors entirely;
 * we do the same.
 */
export function sortFloorsAscending(floors: IProjectFloor[]): IProjectFloor[] {
  const filtered = floors.filter((f) => f.floorId !== 'storage');

  const digits: IProjectFloor[] = [];
  const digitsLetters: IProjectFloor[] = [];
  const pa: IProjectFloor[] = [];
  const other: IProjectFloor[] = [];

  const digitsOnly = /^-?\d+$/;

  for (const f of filtered) {
    if (f.floorId === 'pa') pa.push(f);
    else if (digitsOnly.test(f.floorId)) digits.push(f);
    else if (/\d/.test(f.floorId)) digitsLetters.push(f);
    else other.push(f);
  }

  digits.sort((a, b) => Number(a.floorId) - Number(b.floorId));
  digitsLetters.sort((a, b) => compareFloorIdsWithLetters(a.floorId, b.floorId));
  other.sort((a, b) => a.floorId.localeCompare(b.floorId));

  return [...digits, ...digitsLetters, ...pa, ...other];
}

/**
 * Sort key for floor ids that mix digits + a trailing letter (e.g. `1A`,
 * `2B`): primary key is the letter (alphabetical), secondary is the
 * numeric prefix. Mirrors trusstor's `compareFloorIdsWithLetters`.
 */
function compareFloorIdsWithLetters(a: string, b: string): number {
  const letterA = a.charAt(a.length - 1);
  const letterB = b.charAt(b.length - 1);
  if (letterA === letterB) {
    const numA = Number(a.substring(0, a.length - 1));
    const numB = Number(b.substring(0, b.length - 1));
    return numA - numB;
  }
  return letterA.localeCompare(letterB);
}
